import { useQuery } from "@tanstack/react-query";

import { featuresApi } from "@/lib/api/features";
import { qk } from "@/lib/query-client";
import { useActiveTenant } from "@/stores/auth-store";
import type { EffectiveFeature } from "@/types/domain";

/**
 * The caller's own tenant's resolved Feature Toggle set - loaded once (React
 * Query caches by qk.features.my, so every consumer shares one request) and
 * used by the sidebar/routes to decide what to render.
 *
 * Skipped for a Super Admin with no active tenant: /features/my is a
 * workspace endpoint and would 400 for them, the same guard nav-config's
 * Sidebar already applies to every other tenant-scoped item.
 */
export function useEffectiveFeatures() {
  const tenant = useActiveTenant();
  return useQuery<EffectiveFeature[]>({
    queryKey: qk.features.my,
    queryFn: featuresApi.my,
    enabled: Boolean(tenant),
    staleTime: 60_000,
  });
}

/**
 * Whether one feature is enabled for the caller's tenant.
 *
 * Fails OPEN (returns true) while the query is still loading or for a key
 * this build doesn't recognise - the backend (RequireFeature) is the real
 * security boundary and fails CLOSED on the same cases; hiding a nav item is
 * UX polish, not access control, so a worst case here is a route the backend
 * still correctly 403s, never a hole. See middleware.RequireFeature's own
 * comment for the backend's opposite default.
 */
export function useFeatureEnabled(key?: string): boolean {
  const { data, isLoading } = useEffectiveFeatures();
  if (!key) return true;
  if (isLoading || !data) return true;
  const feature = data.find((f) => f.key === key);
  return feature ? feature.enabled : true;
}
